import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { MenuItem } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';

import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { BaseJournodexEntityModel } from '../../../../Discover/Targeting/models/base-journodex-entity.model';
import { ContactModel } from '../../model/contact.model';
import { EmploymentModel } from '../../../../Discover/Targeting/models/employment.model';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { TwitterAvatarSize } from '../../../Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { DropdownTypesEnum } from '../../../Forms/enums/dropdown-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ManageListDialogComponent } from '../../../../Discover/Targeting/components/TargetingResults/ManageListDialog/manage-list-dialog.component';
import { TargetingRhpDialogService } from '../../services/targeting-rhp-dialog.service';
import { ContactService } from '../../services/contact-service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import {
    AddPrivateDataDialogComponent
} from '../../../../Discover/Targeting/components/TargetingResults/AddPrivateDataDialog/add-private-data-dialog.component';
import { PrivateDataModel } from '../../../../Discover/Targeting/models/private-data.model';
import { OutletContactTabType, TargetingRhpContextsService } from '../../services/targeting-rhp-contexts.service';
import { ResearchRequestDialogComponent } from '../../../Dialogs/components/ResearchRequestDialog/research-request-dialog.component';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { LivenessEnum } from '../../../../api/targeting/enum/liveness.enum';
import { EntityTypeDefaultImageEnum } from '../../../Misc/enums/entity-type-default-image.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';

export enum ScrollTopStateEnum {
    zero,
    partialScroll,
    scrolled
}

@Component({
    selector: 'agpr-entity-business-card',
    templateUrl: 'entity-business-card.component.html',
    styleUrls: ['./entity-business-card.component.scss'],
    standalone: false
})
export class EntityBusinessCardComponent {
    @Input() id: string;
    @Input() set entity(entity: BaseJournodexEntityModel) {
        if (!entity) {
            return;
        }

        this.fullEntity = entity;
        this.contactEntity = <ContactModel>entity;
        this.outletEntity = <OutletModel>entity;
        this.actions = [
            {
                label: this.localizationService.transform('Discover.addPrivateData.buttonLabel'),
                command: () => this.openAddPrivateDataDialog()
            },
            {
                label: this.localizationService.transform('Discover.manageList.header'),
                command: () => this.openManageListDialog()
            }
        ];
        if (entity && entity.entityType === EntityType.CONTACT) {
            this.outletDropdownOptions = this.contactEntity.employmentModels.map(emp => {
                return {
                    label : !!emp?.outlet ? emp?.outlet.name : 'Freelance Work',
                    value : emp
                };
            });

            if (this.contactEntity?.employmentModels?.length) {
                this.selectedEmployment = this.contactEntity.selectedEmployment;
                this.selectedEmploymentDropdown = this.outletDropdownOptions.find(option => option.value.id === this.selectedEmployment?.id);
            }

            if (!this.contactEntity.selectedEmployment) {
                this.selectedEmployment = null;
                this.selectedEmploymentDropdown = null;
            }

            if (!!this.contactEntity.privateContactMetadata) {
                this.actions.shift();
                this.actions.unshift({
                    label: this.localizationService.transform('Discover.targeting.actions.edit'),
                    command: () => this.openContactEditor()
                });
            } else {
                this.actions.unshift({
                    label: this.localizationService.transform('Discover.research.researchRequest'),
                    command: () => this.openResearchRequestDialog()
                });
            }
        }

        if (entity && entity.entityType === EntityType.OUTLET) {
            if (!entity.id || (<OutletModel>entity).liveness !== LivenessEnum.ACTIVE) {
                this.actions.shift();
            }
            this.actions.unshift({
                label: this.localizationService.transform('Discover.research.researchRequest'),
                command: () => this.openResearchRequestDialog()
            });
        }
    }
    @Input() set contactPrivateData(privateData: PrivateDataModel) {
        if (privateData) {
            this._contactPrivateData = privateData;
        }
    }
    @Input() set outletPrivateData(privateData: PrivateDataModel) {
        if (privateData) {
            this._outletPrivateData = privateData;
        }
    }

    @Input() set scrollTop(scrollTop: number) {
        if (scrollTop === undefined || scrollTop === null) {
            return;
        }

        this.setStylesFromScrollTopChange(scrollTop);
    }

    @Input() isRHP: boolean = false;
    @Input() canOpenFullProfile: boolean = true;
    @Input() currentUser: UserModel;
    @Input() fullHeightProfileTakeover: boolean = false;
    @Input() isCondensedMode: boolean = false;
    @Input() showOutletInfo: boolean = false;

    @Output() selectedEmploymentChanged: EventEmitter<EmploymentModel> = new EventEmitter();
    @Output() showContactEditor: EventEmitter<void> = new EventEmitter();

    readonly buttonTypes = ButtonTypesEnum;
    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly entityTypes = EntityType;

    dropdownTypesEnum = DropdownTypesEnum;
    outletContactTabType = OutletContactTabType;
    imageDefault = EntityTypeDefaultImageEnum;

    fullEntity: BaseJournodexEntityModel;
    contactEntity: ContactModel;
    outletEntity: OutletModel;
    _contactPrivateData: PrivateDataModel;
    _outletPrivateData: PrivateDataModel;

    scrollTopStateCache: ScrollTopStateEnum;

    selectedEmployment: EmploymentModel;
    selectedEmploymentDropdown: DropdownOptionModel;
    outletDropdownOptions: DropdownOptionModel[] = [];
    // unused?
    // subjects: JournodexSubjectModel[];
    actions: MenuItem[] = [];

    businessCardClass: string;
    basicInfoClass: string;
    profileTitleClass: string;
    badgesClass: string;
    outletPicClass: string;
    outletNameClass: string;
    outletNoImgClass: string;
    outletThumbnailNoneClass: string;
    outletDropdownClass: string;
    outletTitleClass: string;

    isScrolled: boolean = false;
    showBadges: boolean;
    showOutletPic: boolean;
    isTruncated: boolean = false;

    @ViewChild('contactNameLabel') contactNameLabel: ElementRef;
    @ViewChild('outletNameLabel') outletNameLabel: ElementRef;

    constructor(
        private contactService: ContactService,
        private coreDialogService: CoreDialogService,
        private localizationService: LocalizationService,
        private targetingRhpDialogService: TargetingRhpDialogService,
        // private targetingSearchService: TargetingSearchService,
        private targetingRhpContextsService: TargetingRhpContextsService
    ) {}

    employmentSelectedFromDropdown(employee: EmploymentModel): void {
        this.selectedEmployment = employee;
        this.outletEntity = this.selectedEmployment.outlet;
        // TODO : will have to use event for this or something, don't want to include search service here
        // this.targetingSearchService.setEmploymentForFocusedResult(employee);
        this.selectedEmploymentChanged.emit(this.selectedEmployment);
    }

    setStylesFromScrollTopChange(scrollTop: number) {
        let scrollTopState: ScrollTopStateEnum;
        if (scrollTop === 0) {
            scrollTopState = ScrollTopStateEnum.zero;
        } else if (scrollTop < 80) {
            scrollTopState = ScrollTopStateEnum.partialScroll;
        } else {
            scrollTopState = ScrollTopStateEnum.scrolled;
        }

        if (this.scrollTopStateCache === scrollTopState) {
            return;
        }

        switch (scrollTopState) {
            case ScrollTopStateEnum.zero:
                this.businessCardClass = '';
                this.basicInfoClass = '';
                this.profileTitleClass = '';
                this.badgesClass = '';

                this.outletPicClass = '';
                this.outletNameClass = '';
                this.outletNoImgClass = '';
                this.outletThumbnailNoneClass = '';

                this.outletDropdownClass = '';
                this.outletTitleClass = '';
                this.isScrolled = false;
                break;
            case ScrollTopStateEnum.partialScroll:
                this.outletPicClass = 'agpr-entityBusinessCard-outlet-thumbnail-img--scrolled';
                this.outletNoImgClass = 'agpr-entityBusinessCard-outlet-thumbnail-noimg--scrolled';
                this.outletThumbnailNoneClass = 'agpr-entityBusinessCard-outlet-thumbnail-none--scrolled';

                break;
            case ScrollTopStateEnum.scrolled:
                this.businessCardClass = 'agpr-entityBusinessCard--scrolled';
                this.basicInfoClass = 'agpr-entityBusinessCard-basicInfo--scrolled';
                this.profileTitleClass = 'agpr-entityBusinessCard-jobTitle--scrolled';
                this.badgesClass = 'agpr-entityBusinessCard-badges--scrolled';
                this.outletNameClass = 'agpr-entityBusinessCard-outletName--scrolled';
                this.outletDropdownClass = 'agpr-entityBusinessCard-outletDropdown--scrolled';
                this.outletTitleClass = 'agpr-entityBusinessCard-outlet-jobTitle--scrolled';
                this.isScrolled = true;
                break;
        }

        this.scrollTopStateCache = scrollTopState;
    }

    openContactEditor() {
        this.contactService.setEditingContact(this.contactEntity.id);
        // TODO : Ideally we should update the URL but not lose the "context"
        // so that the user can share the page but still be able to close the overlay and return
        // to his original context
        this.showContactEditor.emit();
    }

    openResearchRequestDialog() {
        const inputs = this.fullEntity.entityType === EntityType.CONTACT
            ? { contact: this.contactEntity }
            : { outlet: this.outletEntity };
        this.coreDialogService.open(ResearchRequestDialogComponent, { inputs: inputs });
    }

    checkTruncatedText(textElement: HTMLElement): void {
        this.isTruncated = textElement.scrollWidth > textElement.clientWidth;
    }

    openAddPrivateDataDialog() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const overlayContainer = document.getElementById('main-content');
        this.coreDialogService.open(AddPrivateDataDialogComponent, {
            inputs: {
                setEntity: (this.fullEntity.entityType === EntityType.CONTACT ? this.contactEntity : this.outletEntity),
                setPrivateData: this.fullEntity.entityType === EntityType.CONTACT ? this._contactPrivateData : this._outletPrivateData,
                currentUser: this.currentUser,
                overlayContainer: overlayContainer,
                animationTrigger: animationTrigger
            },
            outputs: {}
        });
    }

    openManageListDialog() {
        this.coreDialogService.open(
            ManageListDialogComponent,
            {
                inputs: {
                    setEntity: this.fullEntity.entityType === EntityType.CONTACT ?
                        this.contactEntity : this.outletEntity,
                },
                outputs: {
                }
            }
        );
    }

    openFullProfile(entity: BaseJournodexEntityModel): void {
        if (!this.canOpenFullProfile) {
            return;
        }

        this.targetingRhpDialogService.openFullProfile(
            entity.entityType,
            entity.entityType === EntityType.CONTACT ? <ContactModel>entity : null,
            entity.entityType === EntityType.OUTLET ? <OutletModel>entity : null,
            entity.entityType === EntityType.CONTACT ? this._contactPrivateData : null,
            this._outletPrivateData,
            null,
            this.fullHeightProfileTakeover
        );
    }
}
